// FE#313 — editing the inter-step WAIT of a launched drip, honestly.
//
// A drip stores its steps as `[{ stepIndex, template, gapAfter }]`, where a step's
// `gapAfter` is the wait that FOLLOWS it (the delay before the next step). The
// SequenceDetail lets the user change that wait in-flight. The catch the read-only
// state used to guard: each step materializes into a real `campaigns` doc whose
// `schedule.dueDate` is FIXED at materialization and never re-derived from the gap.
// So moving `gapAfter` alone shifts the on-screen estimate while the campaign still
// fires at the old time — an edit that looks like it took and did not.
//
// These two pure helpers make the edit honest: `applyGapAfterStep` produces the new
// steps array (the source of truth for future materialization), and
// `projectStepSendDates` walks the sequence forward from its base send time so the
// caller can re-stamp `schedule.dueDate` on every already-materialized, not-yet-sent
// downstream step-campaign. Kept pure (no React, no store) so the gap math is
// unit-tested without a DOM.

import { add } from 'date-fns';
import type { DripStep, DripStepGap } from '@bt-common/schemas/bt/drip';

// A drip gap → a date-fns Duration (calendar-aware for month/year). Mirrors
// sequence-detail's own `gapToDuration`; duplicated here (a ~6-line switch) so this
// pure module carries no import cycle back into the big component.
export const gapToDuration = (gap: DripStepGap): Duration => {
  switch (gap.unit) {
    case 'minute': return { minutes: gap.value };
    case 'hour': return { hours: gap.value };
    case 'day': return { days: gap.value };
    case 'week': return { weeks: gap.value };
    case 'month': return { months: gap.value };
    case 'year': return { years: gap.value };
    default: return {};
  }
};

/**
 * Immutably set the wait that FOLLOWS the step whose `stepIndex === stepIndex` to
 * `gap`. Matches by the step's `stepIndex` field (NOT array position), since a
 * drip's steps are keyed by that. Returns a fresh array (shallow-cloned steps); if
 * no step carries that index the array is returned unchanged (cloned), never
 * throwing — a stale click must not corrupt the sequence.
 */
export function applyGapAfterStep(
  steps: readonly DripStep[] | undefined,
  stepIndex: number,
  gap: DripStepGap,
): DripStep[] {
  const src = steps ?? [];
  return src.map((s) => (s.stepIndex === stepIndex ? { ...s, gapAfter: gap } : { ...s }));
}

/**
 * Walk the sequence forward from `baseAt` (step 0's send time) and return the
 * projected send time of every step, keyed by `stepIndex`. Each later step sends
 * its predecessor's `gapAfter` after the predecessor. Steps are visited in
 * ascending `stepIndex` order regardless of array order, so the projection is
 * stable. A missing `gapAfter` contributes no delay (co-scheduled).
 */
export function projectStepSendDates(
  steps: readonly DripStep[] | undefined,
  baseAt: Date | undefined,
): Map<number, Date> {
  const out = new Map<number, Date>();
  if (!baseAt) return out;
  const ordered = [...(steps ?? [])].sort((a, b) => a.stepIndex - b.stepIndex);
  let running: Date = baseAt;
  for (let i = 0; i < ordered.length; i++) {
    const step = ordered[i];
    out.set(step.stepIndex, running);
    // advance the clock by THIS step's own gapAfter for the next step
    if (step.gapAfter) running = add(running, gapToDuration(step.gapAfter));
  }
  return out;
}
